<div class="dash">

  <div class="dash__header">
    <div>
      <h1 class="dash__title">{{ 'SALESPERSON_DASHBOARD' | translate }}</h1>
      <p class="dash__subtitle">{{ 'SALESPERSON_DASHBOARD_SUBTITLE' | translate }}</p>
    </div>
  </div>

  <!-- Loader -->
  <div *ngIf="loading" class="dash__loader">
    <div class="gung-loader">
      <span class="gung-loader__dot"></span>
      <span class="gung-loader__dot"></span>
      <span class="gung-loader__dot"></span>
    </div>
  </div>

  <div *ngIf="!loading">

    <!-- KPI period comparison cards -->
    <div class="kpi-grid">
      <div class="kpi-card"
        *ngFor="let card of kpiPeriodCards; let i = index"
        [class.kpi-card--up]="card.delta > 0"
        [class.kpi-card--down]="card.delta < 0"
        [style.animation-delay.ms]="80 + i * 70">

        <div class="kpi-card__top">
          <span class="kpi-card__label">{{ card.labelKey | translate }}</span>
          <span class="kpi-card__badge"
            [class.kpi-card__badge--up]="card.delta > 0"
            [class.kpi-card__badge--down]="card.delta < 0"
            *ngIf="card.current">
            <ng-container *ngIf="card.delta > 0">↑ {{ card.delta }}%</ng-container>
            <ng-container *ngIf="card.delta < 0">↓ {{ card.delta }}%</ng-container>
            <ng-container *ngIf="card.delta === 0">–</ng-container>
          </span>
        </div>

        <div class="kpi-card__value">
          <ng-container *ngIf="card.current; else noData">
            {{ formatValue(card.displayAmount) }}
          </ng-container>
          <ng-template #noData><span class="kpi-card__empty">–</span></ng-template>
        </div>

        <div class="kpi-card__meta" *ngIf="card.current">
          <div class="kpi-card__meta-row">
            <span class="kpi-card__orders">{{ card.current.invoiceCount }} {{ 'ORDERS' | translate }}</span>
            <span class="kpi-card__avg" *ngIf="avgOrder(card.current)">
              {{ 'AVG_ORDER' | translate }} {{ formatValue(avgOrder(card.current)) }}
            </span>
          </div>
          <div class="kpi-card__meta-row kpi-card__meta-row--prev" *ngIf="card.previous">
            <span class="kpi-card__prev-orders">{{ card.previous.invoiceCount }} {{ 'PREV_ORDERS' | translate }}</span>
            <span class="kpi-card__prev">{{ formatValue(card.previous.totalValue) }}</span>
          </div>
        </div>

      </div>
    </div>

    <!-- Budget comparison -->
    <div class="budget-grid" *ngIf="budgetMonthCard || budgetYtdCard">

      <!-- Monthly budget card -->
      <div class="budget-card" *ngIf="budgetMonthCard"
        [class.budget-card--on-track]="budgetMonthCard.pct >= 80"
        [class.budget-card--warn]="budgetMonthCard.pct >= 50 && budgetMonthCard.pct < 80"
        [class.budget-card--behind]="budgetMonthCard.pct < 50">

        <div class="budget-card__top">
          <div class="budget-card__meta">
            <span class="budget-card__label">{{ 'BUDGET_MONTH' | translate }}</span>
            <span class="budget-card__period">{{ budgetMonthCard.periodLabel }}</span>
          </div>
          <div class="budget-card__hero"
            [class.budget-card__hero--on-track]="budgetMonthCard.pct >= 80"
            [class.budget-card__hero--warn]="budgetMonthCard.pct >= 50 && budgetMonthCard.pct < 80"
            [class.budget-card__hero--behind]="budgetMonthCard.pct < 50">
            {{ budgetMonthDisplayPct }}<span class="budget-card__hero-unit">%</span>
          </div>
        </div>

        <div class="budget-bar-wrap">
          <div class="budget-bar">
            <div class="budget-bar__fill"
              [style.width.%]="Math.min(budgetMonthCard.pct, 100)"
              [class.budget-bar__fill--on-track]="budgetMonthCard.pct >= 80"
              [class.budget-bar__fill--warn]="budgetMonthCard.pct >= 50 && budgetMonthCard.pct < 80"
              [class.budget-bar__fill--behind]="budgetMonthCard.pct < 50">
            </div>
          </div>
          <div class="budget-bar__labels">
            <span>0</span><span>{{ formatValue(budgetMonthCard.budget) }}</span>
          </div>
        </div>

        <div class="budget-card__figures">
          <div class="budget-card__fig">
            <span class="budget-card__fig-label">{{ 'ACTUAL' | translate }}</span>
            <span class="budget-card__fig-val">{{ formatValue(budgetMonthCard.actual) }}</span>
          </div>
          <div class="budget-card__fig budget-card__fig--muted">
            <span class="budget-card__fig-label">{{ 'BUDGET' | translate }}</span>
            <span class="budget-card__fig-val">{{ formatValue(budgetMonthCard.budget) }}</span>
          </div>
        </div>

        <div class="budget-card__chip"
          [class.budget-card__chip--positive]="budgetMonthCard.remaining <= 0"
          [class.budget-card__chip--warn]="budgetMonthCard.pct >= 50 && budgetMonthCard.pct < 80 && budgetMonthCard.remaining > 0"
          [class.budget-card__chip--behind]="budgetMonthCard.pct < 50">
          <ng-container *ngIf="budgetMonthCard.remaining > 0">
            ↑ {{ formatValue(budgetMonthCard.remaining) }} {{ 'REMAINING_TO_BUDGET' | translate }}
          </ng-container>
          <ng-container *ngIf="budgetMonthCard.remaining <= 0">
            ✓ {{ formatValue(Math.abs(budgetMonthCard.remaining)) }} {{ 'OVER_BUDGET' | translate }}
          </ng-container>
        </div>
      </div>

      <!-- YTD budget card -->
      <div class="budget-card" *ngIf="budgetYtdCard"
        [class.budget-card--on-track]="budgetYtdCard.pct >= 80"
        [class.budget-card--warn]="budgetYtdCard.pct >= 50 && budgetYtdCard.pct < 80"
        [class.budget-card--behind]="budgetYtdCard.pct < 50">

        <div class="budget-card__top">
          <div class="budget-card__meta">
            <span class="budget-card__label">{{ 'BUDGET_YTD' | translate }}</span>
            <span class="budget-card__period">{{ budgetYtdCard.periodLabel }}</span>
          </div>
          <div class="budget-card__hero"
            [class.budget-card__hero--on-track]="budgetYtdCard.pct >= 80"
            [class.budget-card__hero--warn]="budgetYtdCard.pct >= 50 && budgetYtdCard.pct < 80"
            [class.budget-card__hero--behind]="budgetYtdCard.pct < 50">
            {{ budgetYtdDisplayPct }}<span class="budget-card__hero-unit">%</span>
          </div>
        </div>

        <div class="budget-bar-wrap">
          <div class="budget-bar">
            <div class="budget-bar__fill"
              [style.width.%]="Math.min(budgetYtdCard.pct, 100)"
              [class.budget-bar__fill--on-track]="budgetYtdCard.pct >= 80"
              [class.budget-bar__fill--warn]="budgetYtdCard.pct >= 50 && budgetYtdCard.pct < 80"
              [class.budget-bar__fill--behind]="budgetYtdCard.pct < 50">
            </div>
          </div>
          <div class="budget-bar__labels">
            <span>0</span><span>{{ formatValue(budgetYtdCard.budget) }}</span>
          </div>
        </div>

        <div class="budget-card__figures">
          <div class="budget-card__fig">
            <span class="budget-card__fig-label">{{ 'ACTUAL' | translate }}</span>
            <span class="budget-card__fig-val">{{ formatValue(budgetYtdCard.actual) }}</span>
          </div>
          <div class="budget-card__fig budget-card__fig--muted">
            <span class="budget-card__fig-label">{{ 'BUDGET' | translate }}</span>
            <span class="budget-card__fig-val">{{ formatValue(budgetYtdCard.budget) }}</span>
          </div>
        </div>

        <div class="budget-card__chip"
          [class.budget-card__chip--positive]="budgetYtdCard.remaining <= 0"
          [class.budget-card__chip--warn]="budgetYtdCard.pct >= 50 && budgetYtdCard.pct < 80 && budgetYtdCard.remaining > 0"
          [class.budget-card__chip--behind]="budgetYtdCard.pct < 50">
          <ng-container *ngIf="budgetYtdCard.remaining > 0">
            ↑ {{ formatValue(budgetYtdCard.remaining) }} {{ 'REMAINING_TO_BUDGET' | translate }}
          </ng-container>
          <ng-container *ngIf="budgetYtdCard.remaining <= 0">
            ✓ {{ formatValue(Math.abs(budgetYtdCard.remaining)) }} {{ 'OVER_BUDGET' | translate }}
          </ng-container>
        </div>

        <div class="budget-card__yearline">
          <span class="budget-card__yearline-label">{{ 'OF_FULL_YEAR_BUDGET' | translate }}</span>
          <div class="budget-card__yearline-right">
            <div class="budget-year-bar">
              <div class="budget-year-bar__fill" [style.width.%]="Math.min(budgetYearPct, 100)"></div>
            </div>
            <span class="budget-card__yearline-val">{{ budgetYearPct }}%</span>
          </div>
        </div>

      </div>

    </div>

    <!-- Today vs previous business day -->
    <div class="kpi-grid kpi-grid--2col" *ngIf="todayRow || prevDayRow">
      <div class="kpi-card"
        [class.kpi-card--up]="todayRow && prevDayRow && delta(+todayRow.totalValue, +prevDayRow.totalValue) > 0"
        [class.kpi-card--down]="todayRow && prevDayRow && delta(+todayRow.totalValue, +prevDayRow.totalValue) < 0">

        <div class="kpi-card__top">
          <span class="kpi-card__label">{{ todayDateLabel }}</span>
          <span class="kpi-card__badge"
            *ngIf="todayRow && prevDayRow"
            [class.kpi-card__badge--up]="delta(+todayRow.totalValue, +prevDayRow.totalValue) > 0"
            [class.kpi-card__badge--down]="delta(+todayRow.totalValue, +prevDayRow.totalValue) < 0">
            <ng-container *ngIf="delta(+todayRow.totalValue, +prevDayRow.totalValue) > 0">↑ {{ delta(+todayRow.totalValue, +prevDayRow.totalValue) }}%</ng-container>
            <ng-container *ngIf="delta(+todayRow.totalValue, +prevDayRow.totalValue) < 0">↓ {{ delta(+todayRow.totalValue, +prevDayRow.totalValue) }}%</ng-container>
            <ng-container *ngIf="delta(+todayRow.totalValue, +prevDayRow.totalValue) === 0">–</ng-container>
          </span>
        </div>

        <div class="kpi-card__value">
          <ng-container *ngIf="todayRow; else noToday">{{ formatValue(+todayRow.totalValue) }}</ng-container>
          <ng-template #noToday><span class="kpi-card__empty">–</span></ng-template>
        </div>

        <div class="kpi-card__meta" *ngIf="todayRow || prevDayRow">
          <div class="kpi-card__meta-row" *ngIf="todayRow">
            <span class="kpi-card__orders">{{ todayRow.orderCount }} {{ 'ORDERS' | translate }}</span>
            <span class="kpi-card__avg" *ngIf="todayRow.orderCount">
              {{ 'AVG_ORDER' | translate }} {{ formatValue(Math.round(+todayRow.totalValue / +todayRow.orderCount)) }}
            </span>
          </div>
          <div class="kpi-card__meta-row kpi-card__meta-row--prev" *ngIf="prevDayRow">
            <span class="kpi-card__prev-orders">{{ prevDayRow.orderCount }} {{ 'PREV_ORDERS' | translate }} · {{ prevDayDateLabel }}</span>
            <span class="kpi-card__prev">{{ formatValue(+prevDayRow.totalValue) }}</span>
          </div>
        </div>

      </div>
    </div>

    <!-- Daily sales chart -->
    <div class="dash__card dash__card--chart" *ngIf="dailySalesDatasets.length">
      <div class="card__head">
        <h2 class="card__label">{{ 'DAILY_SALES' | translate }}</h2>
      </div>
      <div class="card__body">
        <div class="chart-wrap">
          <canvas baseChart
            [datasets]="dailySalesDatasets"
            [labels]="dailySalesLabels"
            [options]="dailySalesOptions"
            type="bar">
          </canvas>
        </div>
      </div>
    </div>

  </div>
</div>
